import React, { useRef, useEffect, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { RECAPTCHA_CONFIG, RECAPTCHA_ERRORS } from '../../config/recaptcha';

// Extend Window interface for grecaptcha
declare global {
  interface Window {
    grecaptcha: any;
  }
}

interface RecaptchaProps {
  onVerify: (token: string | null) => void;
  onError?: (error: string) => void;
  theme?: 'light' | 'dark';
  size?: 'normal' | 'compact' | 'invisible';
  className?: string;
}

const Recaptcha: React.FC<RecaptchaProps> = ({
  onVerify,
  onError,
  theme = RECAPTCHA_CONFIG.THEME,
  size = RECAPTCHA_CONFIG.SIZE,
  className = ''
}) => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if reCAPTCHA script is loaded
    const checkRecaptchaLoaded = () => {
      if (window.grecaptcha && window.grecaptcha.ready) {
        setIsLoaded(true);
      } else {
        setTimeout(checkRecaptchaLoaded, 100);
      }
    };
    checkRecaptchaLoaded();
  }, []);

  const handleChange = (token: string | null) => {
    setError(null);
    onVerify(token);
  };

  const handleError = () => {
    const errorMessage = RECAPTCHA_ERRORS.NETWORK_ERROR;
    setError(errorMessage);
    onError?.(errorMessage);
  };

  const handleExpired = () => {
    const errorMessage = RECAPTCHA_ERRORS.EXPIRED_TOKEN;
    setError(errorMessage);
    onError?.(errorMessage);
  };

  const reset = () => {
    recaptchaRef.current?.reset();
    setError(null);
  };

  if (!RECAPTCHA_CONFIG.SITE_KEY || RECAPTCHA_CONFIG.SITE_KEY.includes('XXXXX')) {
    return (
      <div className={`p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg ${className}`}>
        <p className="text-yellow-800 dark:text-yellow-200 text-sm">
          ⚠️ reCAPTCHA not configured. Please add your reCAPTCHA site key to the configuration.
        </p>
      </div>
    );
  }

  return (
    <div className={`recaptcha-wrapper ${className}`}>
      {error && (
        <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
        </div>
      )}
      
      <div className="flex justify-center">
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={RECAPTCHA_CONFIG.SITE_KEY}
          onChange={handleChange}
          onError={handleError}
          onExpired={handleExpired}
          theme={theme}
          size={size}
          className="recaptcha-container"
        />
      </div>
      
      {!isLoaded && (
        <div className="mt-2 text-center">
          <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
          <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading reCAPTCHA...</span>
        </div>
      )}
    </div>
  );
};

export default Recaptcha; 